/**
 * Gestione file firmati CAdES (.p7m).
 *
 * Un file .p7m è un container PKCS#7 che wrappa il file originale.
 * Quando il contenuto è un PDF, lo si può individuare cercando
 * i marker `%PDF` (inizio) e `%%EOF` (fine) nei byte raw,
 * evitando un parser ASN.1 completo.
 */

const P7M_EXTENSION_RE = /\.p7m$/i;
const PDF_HEADER = [0x25, 0x50, 0x44, 0x46]; // %PDF
const EOF_MARKER = [0x25, 0x25, 0x45, 0x4f, 0x46]; // %%EOF

function findSequence(data: Uint8Array, seq: number[], from = 0): number {
  const end = data.length - seq.length;
  for (let i = from; i <= end; i++) {
    if (seq.every((b, j) => data[i + j] === b)) return i;
  }
  return -1;
}

function findSequenceLast(data: Uint8Array, seq: number[]): number {
  for (let i = data.length - seq.length; i >= 0; i--) {
    if (seq.every((b, j) => data[i + j] === b)) return i;
  }
  return -1;
}

export type IP7mExtractionResult =
  | { status: 'pdf'; data: ArrayBuffer; filename: string }
  | { status: 'not-pdf' }
  | { status: 'error' };

/**
 * Verifica se un file è un .p7m basandosi sul nome o mimetype.
 */
export function isP7mFile(filename: string, mimetype?: string): boolean {
  const normalizedMime = mimetype
    ? mimetype.toLowerCase().split(';', 1)[0].trim()
    : undefined;

  if (
    normalizedMime === 'application/pkcs7-mime' ||
    normalizedMime === 'application/x-pkcs7-mime'
  ) {
    return true;
  }

  return filename.toLowerCase().endsWith('.p7m');
}

/**
 * Estrae il PDF contenuto in un file .p7m.
 * - `pdf`: estrazione riuscita
 * - `not-pdf`: il container è valido ma non contiene un PDF
 * - `error`: impossibile leggere il container
 */
export function extractPdfFromP7m(
  data: ArrayBuffer,
  filename: string,
): IP7mExtractionResult {
  try {
    const bytes = new Uint8Array(data);
    // Un PKCS#7 valido inizia con SEQUENCE tag (0x30)
    if (bytes.length < 2 || bytes[0] !== 0x30) return { status: 'error' };

    const pdfStart = findSequence(bytes, PDF_HEADER);
    if (pdfStart === -1) return { status: 'not-pdf' };

    const eofPos = findSequenceLast(bytes, EOF_MARKER);
    if (eofPos === -1 || eofPos < pdfStart) return { status: 'error' };

    const pdfEnd = eofPos + EOF_MARKER.length;
    const pdfBytes = bytes.subarray(pdfStart, pdfEnd);

    const extractedBuffer = new ArrayBuffer(pdfBytes.byteLength);
    new Uint8Array(extractedBuffer).set(pdfBytes);

    return {
      status: 'pdf',
      data: extractedBuffer,
      filename: filename.replace(P7M_EXTENSION_RE, ''),
    };
  } catch {
    return { status: 'error' };
  }
}
